/**
 * WizardNavigation Component
 * Left sidebar navigation for the Model Planner Wizard
 * Shows sections, items, and completion status
 */

import React, { useState } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';

const WizardNavigation = ({
  sections,
  currentSection,
  currentItemIndex,
  currentQuestionId,
  sessionData,
  onSectionSelect,
  onIncompleteWarning,
  onItemClick,
  onSectionSummaryClick,
  wizardEngine
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedItems, setExpandedItems] = useState({}); // Track which items have expanded question lists

  // Auto-expand section when viewing an item within it
  React.useEffect(() => {
    if (currentSection && currentItemIndex !== undefined) {
      setExpandedSections(prev => ({
        ...prev,
        [currentSection]: true
      }));

      // Auto-expand the current item's question list
      const itemKey = `${currentSection}-${currentItemIndex}`;
      setExpandedItems(prev => ({
        ...prev,
        [itemKey]: true
      }));
    }
  }, [currentSection, currentItemIndex]);

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const toggleItem = (sectionKey, itemIndex) => {
    const itemKey = `${sectionKey}-${itemIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  const handleSectionClick = (sectionKey) => {
    // Check if navigating away from incomplete section
    if (currentSection && currentSection !== sectionKey) {
      const isCurrentComplete = isSectionComplete(currentSection);
      if (!isCurrentComplete && onIncompleteWarning) {
        const proceed = onIncompleteWarning(currentSection);
        if (!proceed) return;
      }
    }

    // For looped sections with items, show summary instead of starting over
    const sectionConfig = sections[sectionKey];
    const items = getSectionItems(sectionKey);
    if (sectionConfig.type === 'looped' && items.length > 0 && onSectionSummaryClick) {
      onSectionSummaryClick(sectionKey);
    } else {
      onSectionSelect(sectionKey);
    }

    // Auto-expand clicked section
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: true
    }));
  };

  const isSectionComplete = (sectionKey) => {
    const sectionConfig = sections[sectionKey];
    if (!sectionConfig) return false;

    // Check if section has been answered
    const data = sessionData[getSectionDataKey(sectionKey)];
    if (!data) return false;

    // For looped sections, check if all items are completed
    if (sectionConfig.type === 'looped') {
      return Array.isArray(data) && data.length > 0 && data.every(item => item.completed === true);
    }

    // For single sections, check if completed flag is set
    if (sectionConfig.type === 'single') {
      return data.completed === true;
    }

    return false;
  };

  const getSectionDataKey = (sectionKey) => {
    const keyMap = {
      'ponds': 'ponds',
      'curb': 'curbTypes',
      'grading': 'gradingSections',
      'walls': 'walls',
      'ditches': 'ditchesSwales',
      'sidewalks': 'sidewalks',
      'ada-ramps': 'adaRamps',
      'building-pads': 'buildingPads',
      'grate-elevations': 'grateElevations'
    };
    return keyMap[sectionKey] || sectionKey;
  };

  const getSectionItems = (sectionKey) => {
    const dataKey = getSectionDataKey(sectionKey);
    const data = sessionData[dataKey];

    if (!data) return [];

    // For looped sections, return array of items
    if (Array.isArray(data)) {
      return data.map((item, index) => ({
        id: item.id,
        name: getItemName(sectionKey, item, index),
        complete: item.completed === true
      }));
    }

    return [];
  };

  const getItemName = (sectionKey, item, index) => {
    switch (sectionKey) {
      case 'ponds':
        return item.pond_name || `Pond ${index + 1}`;
      case 'curb':
        return item.curb_type_name || `Curb Type ${index + 1}`;
      case 'walls':
        return item.wall_name || `Wall ${index + 1}`;
      case 'ada-ramps':
        return item.ramp_type || `ADA Ramp ${index + 1}`;
      default:
        return `Item ${index + 1}`;
    }
  };

  return (
    <div className="wizard-navigation">
      <h3 style={{
        margin: '0 0 var(--space-4) 0',
        fontSize: 'var(--text-lg)',
        fontWeight: 'var(--font-semibold)',
        color: 'var(--gray-50)',
        paddingBottom: 'var(--space-3)',
        borderBottom: '1px solid var(--gray-700)'
      }}>
        Sections
      </h3>

      {Object.entries(sections).map(([sectionKey, sectionConfig]) => {
        const isComplete = isSectionComplete(sectionKey);
        const isCurrent = currentSection === sectionKey;
        const isExpanded = expandedSections[sectionKey];
        const items = getSectionItems(sectionKey);
        const hasItems = items.length > 0;

        return (
          <div key={sectionKey} style={{ marginBottom: 'var(--space-2)' }}>
            {/* Section Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: 'var(--space-3)',
                border: '1px solid',
                borderColor: isCurrent ? 'var(--cta-blue)' : (isComplete ? 'rgba(16, 185, 129, 0.3)' : 'var(--gray-700)'),
                backgroundColor: isCurrent ? 'rgba(59, 130, 246, 0.15)' : (isComplete ? 'rgba(16, 185, 129, 0.1)' : 'var(--dark-surface)'),
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                gap: 'var(--space-2)',
                transition: 'all var(--transition-fast)'
              }}
              onClick={() => handleSectionClick(sectionKey)}
              onMouseEnter={(e) => {
                if (!isCurrent) {
                  e.currentTarget.style.backgroundColor = 'var(--gray-700)';
                  e.currentTarget.style.borderColor = 'var(--gray-600)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCurrent) {
                  e.currentTarget.style.backgroundColor = isComplete ? 'rgba(16, 185, 129, 0.1)' : 'var(--dark-surface)';
                  e.currentTarget.style.borderColor = isComplete ? 'rgba(16, 185, 129, 0.3)' : 'var(--gray-700)';
                }
              }}
            >
              {/* Expand/Collapse Icon */}
              {hasItems && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection(sectionKey);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--gray-400)'
                  }}
                >
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              )}

              {/* Completion Checkbox */}
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid',
                borderColor: isComplete ? 'var(--success)' : 'var(--gray-600)',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backgroundColor: isComplete ? 'var(--success)' : 'transparent'
              }}>
                {isComplete && <Check size={14} color="white" />}
              </div>

              {/* Section Label */}
              <span style={{
                fontSize: 'var(--text-sm)',
                fontWeight: isCurrent ? 'var(--font-semibold)' : 'var(--font-medium)',
                color: isCurrent ? 'var(--gray-50)' : 'var(--gray-300)',
                flex: 1
              }}>
                {sectionConfig.label}
              </span>
            </div>

            {/* Section Items (for looped sections) */}
            {hasItems && isExpanded && (
              <div style={{ marginLeft: 'var(--space-5)', marginTop: 'var(--space-2)' }}>
                {items.map((item, index) => {
                  // Get the full item data from sessionData
                  const dataKey = getSectionDataKey(sectionKey);
                  const fullItemData = sessionData[dataKey][index];

                  // Check if this item is currently selected
                  const isCurrentItem = isCurrent && currentItemIndex === index;

                  // Check if item's question list is expanded
                  const itemKey = `${sectionKey}-${index}`;
                  const isItemExpanded = expandedItems[itemKey];

                  // Get navigable questions for this section
                  const navigableQuestions = wizardEngine ? wizardEngine.getNavigableQuestions(sectionKey) : [];

                  return (
                    <div key={item.id || index}>
                      {/* Item Header */}
                      <div
                        style={{
                          padding: 'var(--space-2) var(--space-3)',
                          border: '1px solid',
                          borderColor: isCurrentItem ? 'var(--cta-blue)' : 'var(--gray-700)',
                          backgroundColor: isCurrentItem ? 'rgba(59, 130, 246, 0.15)' : 'var(--dark-elevated)',
                          borderRadius: 'var(--radius-sm)',
                          marginBottom: 'var(--space-2)',
                          fontSize: 'var(--text-sm)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)'
                        }}
                        onMouseEnter={(e) => {
                          if (!isCurrentItem) {
                            e.currentTarget.style.backgroundColor = 'var(--gray-700)';
                            e.currentTarget.style.borderColor = 'var(--gray-600)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isCurrentItem) {
                            e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                            e.currentTarget.style.borderColor = 'var(--gray-700)';
                          }
                        }}
                      >
                        {/* Expand/Collapse Icon */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleItem(sectionKey, index);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--gray-400)'
                          }}
                        >
                          {isItemExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </button>

                        {/* Item content (clickable to view item) */}
                        <div
                          onClick={() => onItemClick && onItemClick(sectionKey, fullItemData, index)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            flex: 1
                          }}
                        >
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid',
                            borderColor: item.complete ? 'var(--success)' : 'var(--gray-600)',
                            borderRadius: 'var(--radius-sm)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            backgroundColor: item.complete ? 'var(--success)' : 'transparent'
                          }}>
                            {item.complete && <Check size={10} color="white" />}
                          </div>
                          <span style={{
                            color: isCurrentItem ? 'var(--gray-50)' : 'var(--gray-300)',
                            fontWeight: isCurrentItem ? 'var(--font-semibold)' : 'var(--font-normal)'
                          }}>{item.name}</span>
                        </div>
                      </div>

                      {/* Question List (expandable) */}
                      {isItemExpanded && navigableQuestions.length > 0 && (
                        <div style={{ marginLeft: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
                          {navigableQuestions.map((question) => {
                            const isCurrentQuestion = isCurrentItem && currentQuestionId === question.id;

                            return (
                              <div
                                key={question.id}
                                onClick={() => onItemClick && onItemClick(sectionKey, fullItemData, index, question.id)}
                                style={{
                                  padding: 'var(--space-1) var(--space-2)',
                                  fontSize: 'var(--text-xs)',
                                  color: isCurrentQuestion ? 'var(--cta-blue)' : 'var(--gray-400)',
                                  fontWeight: isCurrentQuestion ? 'var(--font-semibold)' : 'var(--font-normal)',
                                  cursor: 'pointer',
                                  borderLeft: '2px solid',
                                  borderColor: isCurrentQuestion ? 'var(--cta-blue)' : 'transparent',
                                  marginBottom: 'var(--space-1)',
                                  transition: 'all var(--transition-fast)'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isCurrentQuestion) {
                                    e.currentTarget.style.color = 'var(--gray-200)';
                                    e.currentTarget.style.borderColor = 'var(--gray-600)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isCurrentQuestion) {
                                    e.currentTarget.style.color = 'var(--gray-400)';
                                    e.currentTarget.style.borderColor = 'transparent';
                                  }
                                }}
                              >
                                {question.text}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WizardNavigation;
